import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Alert } from '../components/auth/Alert.js';
import { OtpInput } from '../components/auth/OtpInput.js';
import { AuthLayout } from '../layouts/AuthLayout.js';
import { AUTH_STORAGE_KEYS, mfaActivateRequest, mfaSetupRequest } from '../lib/auth-api.js';
import { copyRecoveryCodes, downloadRecoveryCodesPdf } from '../lib/recovery-codes.js';

type Step1Data = { qrCodeDataUrl: string; secretText: string };
type Step2Data = { recoveryCodes: string[] };
type Stage =
  | { kind: 'loading' }
  | { kind: 'step1'; data: Step1Data }
  | { kind: 'step2'; data: Step2Data }
  | { kind: 'expired' };

export function MfaSetupPage() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = sessionStorage.getItem(AUTH_STORAGE_KEYS.setupToken);
    if (!token) {
      navigate('/login');
      return;
    }
    void (async () => {
      const result = await mfaSetupRequest(token);
      if (!result.ok) {
        setStage({ kind: 'expired' });
        return;
      }
      setStage({
        kind: 'step1',
        data: { qrCodeDataUrl: result.qrCodeDataUrl, secretText: result.secretText },
      });
    })();
  }, [navigate]);

  if (stage.kind === 'loading') {
    return (
      <AuthLayout title="Configuration de la double authentification">
        <p className="text-sm text-ink-secondary">Chargement…</p>
      </AuthLayout>
    );
  }
  if (stage.kind === 'expired') {
    return <ExpiredSetupView />;
  }
  if (stage.kind === 'step1') {
    return (
      <Step1View
        data={stage.data}
        error={error}
        onError={setError}
        onSuccess={(codes) => setStage({ kind: 'step2', data: { recoveryCodes: codes } })}
      />
    );
  }
  return <Step2View data={stage.data} onContinue={() => navigate('/')} />;
}

function ExpiredSetupView() {
  return (
    <AuthLayout title="Session de configuration expirée">
      <Alert variant="error">
        Votre session de configuration a expiré. Reconnectez-vous pour reprendre la mise en place de
        la double authentification.
      </Alert>
      <Link to="/login" className="btn-primary w-full mt-6" data-testid="mfa-setup-back-to-login">
        Retour à la connexion
      </Link>
    </AuthLayout>
  );
}

function Step1View({
  data,
  error,
  onError,
  onSuccess,
}: {
  data: Step1Data;
  error: string | null;
  onError: (msg: string | null) => void;
  onSuccess: (codes: string[]) => void;
}) {
  const [otp, setOtp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [otpResetKey, setOtpResetKey] = useState(0);
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const navigate = useNavigate();

  const canSubmit = !submitting && otp.length === 6;

  async function onSubmit(): Promise<void> {
    if (!canSubmit) return;
    const token = sessionStorage.getItem(AUTH_STORAGE_KEYS.setupToken);
    if (!token) {
      navigate('/login');
      return;
    }
    setSubmitting(true);
    onError(null);
    const result = await mfaActivateRequest(token, otp);
    if (!result.ok) {
      setSubmitting(false);
      setOtp('');
      setOtpResetKey((k) => k + 1);
      if (result.kind === 'invalid_setup_token') {
        onError('Session expirée. Reconnectez-vous pour reprendre la configuration.');
        return;
      }
      if (result.kind === 'invalid_code') {
        onError('Code incorrect, vérifiez sur votre application.');
        return;
      }
      onError(result.message);
      return;
    }
    onSuccess(result.recoveryCodes);
  }

  async function copySecret(): Promise<void> {
    if (!navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(data.secretText);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1800);
    } catch {
      /* ignore */
    }
  }

  return (
    <AuthLayout title="Configuration de la double authentification" subtitle="Étape 1 sur 2">
      {error ? (
        <div className="mb-4">
          <Alert variant="error">{error}</Alert>
        </div>
      ) : null}

      <p className="text-sm text-ink-secondary">
        Scannez ce QR code avec Google Authenticator, Microsoft Authenticator, Authy ou toute autre
        application TOTP compatible. Si vous ne pouvez pas scanner, saisissez la clé manuellement.
      </p>

      <div className="mt-6 flex flex-col items-center gap-4">
        <div className="rounded-card border border-platinum bg-surface p-3">
          <img
            src={data.qrCodeDataUrl}
            alt="QR code TOTP — à scanner avec votre application d’authentification"
            width={208}
            height={208}
            className="block"
            data-testid="mfa-qr"
          />
        </div>

        <div className="w-full">
          <p className="text-xs uppercase tracking-wider text-ink-tertiary mb-1">
            Clé secrète (saisie manuelle)
          </p>
          <div className="flex items-stretch gap-2">
            <code
              className="flex-1 font-tabular text-sm text-ink bg-platinum rounded-card px-3 py-2 select-all break-all"
              data-testid="mfa-secret"
            >
              {data.secretText}
            </code>
            <button
              type="button"
              className="btn-secondary !min-h-0 !px-3 !py-1 text-xs"
              onClick={() => void copySecret()}
              data-testid="mfa-copy-secret"
            >
              {copyState === 'copied' ? 'Copié' : 'Copier'}
            </button>
          </div>
        </div>
      </div>

      <hr className="my-6 border-platinum" />

      <p className="text-sm text-ink mb-3">
        Saisissez le code à 6 chiffres affiché par votre application pour finaliser l’activation.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void onSubmit();
        }}
      >
        <OtpInput
          key={otpResetKey}
          ariaLabel="Code à 6 chiffres de votre application d’authentification"
          onChange={setOtp}
          onComplete={(code) => setOtp(code)}
          autoFocus
        />

        <button
          type="submit"
          disabled={!canSubmit}
          aria-busy={submitting || undefined}
          className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="mfa-activate-submit"
        >
          {submitting ? 'Activation…' : 'Activer la double authentification'}
        </button>
      </form>
    </AuthLayout>
  );
}

function Step2View({ data, onContinue }: { data: Step2Data; onContinue: () => void }) {
  const [confirmed, setConfirmed] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  async function onCopy(): Promise<void> {
    const ok = await copyRecoveryCodes(data.recoveryCodes);
    setCopyState(ok ? 'copied' : 'failed');
    setTimeout(() => setCopyState('idle'), 1800);
  }

  function handleContinue(): void {
    if (!confirmed) return;
    sessionStorage.removeItem(AUTH_STORAGE_KEYS.setupToken);
    onContinue();
  }

  return (
    <AuthLayout
      title="Vos codes de récupération"
      subtitle="Étape 2 sur 2"
      beforeTitle={
        <p className="text-xs uppercase tracking-widest text-ink-tertiary font-medium">
          Sauvegardez avant de continuer
        </p>
      }
    >
      <div className="mb-4">
        <Alert variant="warning" title="Ces codes ne seront plus jamais affichés">
          Sauvegardez-les dès maintenant. Chaque code ne peut être utilisé qu’une seule fois pour
          récupérer l’accès à votre compte si vous perdez votre application d’authentification.
        </Alert>
      </div>

      <div
        className="grid grid-cols-2 gap-x-6 gap-y-2 font-tabular text-sm bg-glacier rounded-card px-4 py-4"
        data-testid="mfa-recovery-codes"
      >
        {data.recoveryCodes.map((code) => (
          <code key={code} className="select-all text-ink">
            {code}
          </code>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => downloadRecoveryCodesPdf(data.recoveryCodes)}
          data-testid="mfa-download-pdf"
        >
          Télécharger en PDF
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => void onCopy()}
          data-testid="mfa-copy-codes"
        >
          {copyState === 'copied' ? 'Copié' : copyState === 'failed' ? 'Échec copie' : 'Copier'}
        </button>
      </div>

      <label className="mt-6 flex items-start gap-3 text-sm text-ink cursor-pointer">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-graphite"
          data-testid="mfa-confirm-checkbox"
        />
        <span>Je confirme avoir sauvegardé ces codes en lieu sûr.</span>
      </label>

      <button
        type="button"
        onClick={handleContinue}
        disabled={!confirmed}
        className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="mfa-continue"
      >
        Continuer vers l’application
      </button>
    </AuthLayout>
  );
}
